/**
 * ExpandedValueDialog Component
 *
 * A unified dialog for viewing/editing JSON fields and multiline text values.
 * Supports mode switching between JSON (Monaco editor) and text (textarea) modes.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { AlertCircle, Check, Copy, FileJson, FileText, X, Pencil, Code2, ListTree } from 'lucide-react';
import Editor from '@monaco-editor/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Textarea } from '../../../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import { openConfirmDialog } from '../../common/components/confirm/confirmDialogStore';
import { openDeclareJsonColumnDialog } from '../../common/components/filters/declareJsonColumnDialogStore';
import { useAppStore } from '../../../../store/appStore';
import { useAnalytics } from '../../../../core/analytics';
import type { ExpandedEditMode } from '../utils/expandedEditDetection';
import { formatValueForExpandedEdit } from '../utils/expandedEditDetection';
import { JsonTreeView } from '../../common/components/JsonTreeView';
import { useMetaInfo, useInvalidateMetaInfo } from '../../common/hooks/useMetaInfo';
import { removeJsonColumn } from '../../common/services/metaInfoApi';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { toast } from '../../../../components/ui/toaster';
import { writeClipboardText } from '../../../../lib/clipboard';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { ExpandedSpatialPayload } from '../../common/utils/spatialValueAccessor';
import { SpatialGeometryPreview } from '../../spatial/map/SpatialGeometryPreview';
import { ensureJamMonacoThemes } from '../../../../core/theme/monacoTheme';

type JsonView = 'tree' | 'code';

type ExpandedTab = 'json-code' | 'json-tree' | 'text';

/**
 * When the dialog is opened from a declared-JSON column (native or
 * user-declared via MetaInfo), the caller passes this context so the dialog
 * can offer an "Undeclare as JSON" affordance for user-declared columns.
 * The dialog looks up the matching `JsonColumnDeclaration` in MetaInfo
 * itself — callers don't need to resolve `declarationId` upfront.
 */
export type DeclaredJsonContext = {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  column: string;
  /** Surface label for the analytics event when present. */
  engine?: DatabaseEngineId;
};

/**
 * Context for the "Declare column as JSON?" prompt shown when the current
 * cell value parses as JSON but the column is neither declared nor native.
 * Caller (the grid) is responsible for only setting this when categorization
 * says `json-eligible` AND column-level heuristic was positive — otherwise
 * leave undefined and no prompt renders.
 */
export type DeclarableJsonContext = {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  column: string;
  /** Engine-specific column data type string (passed through to the dialog). */
  dataType?: string;
  engine: DatabaseEngineId;
};

type ExpandedValueDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValue: string;
  initialMode: ExpandedEditMode;
  onConfirm: (value: string) => void;
  title: string;
  columnDataType?: string;
  allowModeSwitch?: boolean;
  /** Read-only mode - shows Edit button to switch to edit mode */
  readOnly?: boolean;
  /** Callback when user wants to edit (switches from read-only to edit) */
  onRequestEdit?: () => void;
  /**
   * Set when the dialog was opened from a JSON-declared column. Enables the
   * "Undeclare as JSON" footer link for user-declared (non-native) columns.
   * The dialog resolves the declaration id from MetaInfo on the fly.
   */
  declaredJsonContext?: DeclaredJsonContext;
  /**
   * Set when the dialog was opened from a non-declared JSON-eligible column
   * with column-level JSON evidence. When the current cell value also parses
   * as JSON, the dialog renders a "Declare column as JSON?" inline banner.
   * Ignored when `declaredJsonContext` is set.
   */
  declarableJsonContext?: DeclarableJsonContext;
  /**
   * T11 — present when the dialog was opened from a spatial cell. Renders a map
   * preview pane above the text editor. The pane decodes `spatial.input` (the
   * ORIGINAL wire value — never a pre-stringified string) directly; the text
   * pane / `initialValue` are unchanged.
   */
  spatial?: ExpandedSpatialPayload;
};

/**
 * Validate JSON content and return error message if invalid
 */
function validateJson(value: string): string | null {
  if (!value.trim()) {
    return null; // Empty is valid (will be treated as null)
  }

  try {
    JSON.parse(value);
    return null;
  } catch (error) {
    if (error instanceof Error) {
      return error.message;
    }
    return 'Invalid JSON';
  }
}

export function ExpandedValueDialog({
  open,
  onOpenChange,
  initialValue,
  initialMode,
  onConfirm,
  title,
  allowModeSwitch = true,
  readOnly = false,
  onRequestEdit,
  declaredJsonContext,
  declarableJsonContext,
  spatial,
}: ExpandedValueDialogProps) {
  const darkMode = useAppStore((state) => state.isDarkMode);
  // Persisted Tree-vs-Code preference for JSON cells. We mirror it into local
  // state so toggles inside one dialog instance don't trigger a remount via
  // the open-effect's reset path.
  const persistedJsonView = useAppStore((state) => state.expandedJsonView);
  const setPersistedJsonView = useAppStore((state) => state.setExpandedJsonView);
  const { track } = useAnalytics();
  const [mode, setMode] = useState<ExpandedEditMode>(initialMode);
  const [value, setValue] = useState(initialValue);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(!readOnly);
  const [copied, setCopied] = useState(false);
  // Default to the persisted preference (Code by default, see appStore).
  const [jsonView, setJsonView] = useState<JsonView>(persistedJsonView);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // MetaInfo lookup for the "Undeclare as JSON" affordance — only fetches
  // when a context is provided, otherwise stays disabled.
  const metaInfoQuery = useMetaInfo(
    declaredJsonContext?.connectionId ?? null,
    declaredJsonContext?.database ?? null,
  );
  const invalidateMetaInfo = useInvalidateMetaInfo();
  const matchingJsonDeclaration = useMemo(() => {
    if (!declaredJsonContext) return null;
    const list = metaInfoQuery.data?.jsonColumns ?? [];
    return (
      list.find(
        (decl) =>
          decl.schema === declaredJsonContext.schema &&
          decl.table === declaredJsonContext.table &&
          decl.column === declaredJsonContext.column,
      ) ?? null
    );
  }, [declaredJsonContext, metaInfoQuery.data]);

  // Track the latest persisted preference without re-triggering the reset
  // effect on every toggle (toggles call `setPersistedJsonView`, which would
  // otherwise wipe in-progress edits via the effect's reset logic).
  const persistedJsonViewRef = useRef(persistedJsonView);
  useEffect(() => {
    persistedJsonViewRef.current = persistedJsonView;
  }, [persistedJsonView]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setMode(initialMode);
      // Format the value for the initial mode
      setValue(formatValueForExpandedEdit(initialValue, initialMode));
      setJsonError(null);
      setIsEditing(!readOnly);
      setJsonView(persistedJsonViewRef.current);
      setCopied(false);
    }
  }, [open, initialValue, initialMode, readOnly]);

  // Focus the textarea when entering edit mode in text mode (initial autoFocus
  // wouldn't fire when toggling from view → edit after mount).
  useEffect(() => {
    if (isEditing && mode === 'text') {
      const t = setTimeout(() => textareaRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isEditing, mode]);

  const activeTab: ExpandedTab =
    mode === 'text' ? 'text' : jsonView === 'tree' ? 'json-tree' : 'json-code';

  const handleTabChange = useCallback(
    (next: string) => {
      const tab = next as ExpandedTab;
      if (tab === activeTab) return;

      if (tab === 'text') {
        setMode('text');
        return;
      }

      // Switching to a JSON tab — pretty-print if we're moving from text mode
      // and the content parses; the validation effect surfaces the error
      // otherwise.
      if (mode !== 'json') {
        try {
          const parsed = JSON.parse(value);
          setValue(JSON.stringify(parsed, null, 2));
        } catch {
          // Keep as-is when not valid JSON.
        }
        setMode('json');
      }

      const nextView: JsonView = tab === 'json-tree' ? 'tree' : 'code';
      setJsonView(nextView);
      setPersistedJsonView(nextView);
      track('feature:expanded_json_view', { view: nextView });
    },
    [activeTab, mode, value, track, setPersistedJsonView],
  );

  const handleUndeclareJson = useCallback(async () => {
    if (!declaredJsonContext || !matchingJsonDeclaration) return;
    await removeJsonColumn(
      declaredJsonContext.connectionId,
      declaredJsonContext.database,
      matchingJsonDeclaration.id,
    );
    await invalidateMetaInfo(
      declaredJsonContext.connectionId,
      declaredJsonContext.database,
    );
    const removed = useWorkspaceStore.getState().removeJsonOrphanFilters({
      connectionId: declaredJsonContext.connectionId,
      database: declaredJsonContext.database,
      schema: declaredJsonContext.schema,
      table: declaredJsonContext.table,
      column: declaredJsonContext.column,
    });
    if (removed > 0) {
      toast.success({
        title: `Removed ${removed} JSON filter${removed === 1 ? '' : 's'} on ${declaredJsonContext.column}`,
      });
    }
    track('feature:json_undeclare', {
      source: 'expanded-dialog',
      ...(declaredJsonContext.engine ? { engine: declaredJsonContext.engine } : {}),
    });
    onOpenChange(false);
  }, [declaredJsonContext, matchingJsonDeclaration, invalidateMetaInfo, track, onOpenChange]);

  // Parse the value for the tree view. We compute this regardless of `mode`
  // so the unified tab strip can disable "JSON (Tree)" when the content isn't
  // a parseable JSON container (Tree only makes sense for objects/arrays).
  const parsedJson = useMemo<{ ok: true; value: unknown } | { ok: false }>(() => {
    if (!value.trim()) return { ok: false };
    try {
      return { ok: true, value: JSON.parse(value) };
    } catch {
      return { ok: false };
    }
  }, [value]);

  const parsedJsonIsContainer =
    parsedJson.ok && parsedJson.value !== null && typeof parsedJson.value === 'object';

  // "Declare as JSON?" prompt — shown when the cell value parses as JSON but
  // the column is neither declared nor native.
  const cellParsesAsJson = useMemo(() => {
    if (!declarableJsonContext) return false;
    if (declaredJsonContext) return false;
    const v = initialValue.trim();
    if (v.length < 2) return false;
    if (!(v.startsWith('{') || v.startsWith('['))) return false;
    try {
      JSON.parse(v);
      return true;
    } catch {
      return false;
    }
  }, [declarableJsonContext, declaredJsonContext, initialValue]);

  const promptSeenRef = useRef(false);
  useEffect(() => {
    if (!open) {
      promptSeenRef.current = false;
      return;
    }
    if (cellParsesAsJson && !promptSeenRef.current) {
      promptSeenRef.current = true;
      track('feature:json_declare_prompt_seen', { surface: 'expand_dialog' });
    }
  }, [open, cellParsesAsJson, track]);

  // Validate JSON when in JSON mode
  useEffect(() => {
    if (mode === 'json') {
      setJsonError(validateJson(value));
    } else {
      setJsonError(null);
    }
  }, [mode, value]);

  const handleConfirm = useCallback(() => {
    // In JSON mode, validate before confirming
    if (mode === 'json' && jsonError) {
      return;
    }

    // Minify JSON before saving (remove extra whitespace)
    let finalValue = value;
    if (mode === 'json' && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        finalValue = JSON.stringify(parsed);
      } catch {
        // Keep as-is if not valid (shouldn't happen due to validation)
      }
    }

    // Let the parent handle closing the dialog and any follow-up actions
    onConfirm(finalValue);
  }, [mode, value, jsonError, onConfirm]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleCopy = useCallback(async () => {
    if (await writeClipboardText(value)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return;
    }
    // Fall through to the legacy execCommand path below.
    const textArea = document.createElement('textarea');
    textArea.value = value;
    document.body.appendChild(textArea);
    textArea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(textArea);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [value]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Ctrl/Cmd + Enter to save
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
    // Escape to cancel — let Monaco handle Escape only when it's actually the
    // active editor (JSON mode + Code view + editing). Tree view and text mode
    // close on Escape so users aren't stranded.
    const monacoOwnsEscape = mode === 'json' && jsonView === 'code' && isEditing;
    if (e.key === 'Escape' && !monacoOwnsEscape) {
      e.preventDefault();
      handleCancel();
    }
  }, [handleConfirm, handleCancel, mode, jsonView, isEditing]);

  // Determine if we should show mode switcher
  const showModeSwitcher = useMemo(() => allowModeSwitch, [allowModeSwitch]);

  // Stats for footer
  const stats = useMemo(() => {
    const lines = value.split('\n').length;
    const chars = value.length;
    return { lines, chars };
  }, [value]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] flex flex-col"
        onKeyDown={handleKeyDown}
        data-testid="expanded-edit-dialog"
        // GlobalConfirmDialog mounts the AlertDialog at the app root (sibling
        // of this Dialog). Suppress outside-interaction dismissal when the
        // target lives inside a Radix AlertDialog, otherwise this dialog
        // slams shut on Confirm/Cancel and leaves body pointer-events stuck.
        onPointerDownOutside={(e) => {
          const target = e.target as HTMLElement | null;
          if (target?.closest('[role="alertdialog"]')) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement | null;
          if (target?.closest('[role="alertdialog"]')) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-2">
            <DialogTitle className="flex items-center gap-2 min-w-0">
              {mode === 'json' ? (
                <FileJson className="h-5 w-5" aria-hidden="true" />
              ) : (
                <FileText className="h-5 w-5" aria-hidden="true" />
              )}
              <span className="truncate">{title}</span>
            </DialogTitle>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={handleCopy}
              title={copied ? 'Copied' : 'Copy to clipboard'}
              aria-label="Copy value to clipboard"
              data-testid="expanded-edit-copy-button"
            >
              {copied ? <Check className="h-4 w-4 text-green-500" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
            </Button>
          </div>
          <DialogDescription>
            {!isEditing
              ? mode === 'json'
                ? 'Viewing JSON content.'
                : 'Viewing text content.'
              : mode === 'json'
                ? 'Edit JSON content with syntax highlighting and validation.'
                : 'Edit multiline text content.'}
            {isEditing && <span className="text-muted-foreground"> Press Ctrl&nbsp;+ Enter to save.</span>}
          </DialogDescription>
        </DialogHeader>

        {spatial?.input && (
          <div
            className="rounded-md border border-border bg-muted/20 p-2"
            data-testid="expanded-spatial-map-pane"
          >
            <SpatialGeometryPreview
              value={spatial.input.value}
              engine={spatial.engine}
              dataType={spatial.dataType}
              fetchFullValue={spatial.fetchFullValue}
              enabled={open}
              showCopy
              connectionId={spatial.connectionId}
              database={spatial.database}
              onCopy={(format) => track('feature:spatial_copy', { format })}
            />
          </div>
        )}

        {cellParsesAsJson && declarableJsonContext && (
          <div
            className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs"
            data-testid="expand-dialog-json-declare-prompt"
          >
            <span className="font-mono text-muted-foreground">{'{}?'}</span>
            <span className="flex-1">This cell looks like JSON.</span>
            <Button
              size="sm"
              variant="default"
              onClick={() => {
                track('feature:json_declare_prompt_click', {
                  surface: 'expand_dialog',
                });
                openDeclareJsonColumnDialog({
                  connectionId: declarableJsonContext.connectionId,
                  database: declarableJsonContext.database,
                  schema: declarableJsonContext.schema,
                  table: declarableJsonContext.table,
                  column: declarableJsonContext.column,
                  dataType: declarableJsonContext.dataType,
                  engine: declarableJsonContext.engine,
                  source: 'expand-dialog-prompt',
                  mode: 'eligible',
                });
                onOpenChange(false);
              }}
              data-testid="expand-dialog-json-declare-cta"
            >
              Declare column as JSON
            </Button>
          </div>
        )}

        {/* Combined tab strip: JSON (Code) — Monaco with JSON syntax + the
            default — JSON (Tree), and Text. Tree is disabled when the value
            doesn't parse as a JSON container (object/array). */}
        {showModeSwitcher && (
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="w-fit"
          >
            <TabsList className="h-8" data-testid="expanded-edit-tab-strip">
              <TabsTrigger
                value="json-code"
                className="text-xs px-3 h-6"
                data-testid="expanded-edit-json-view-code"
              >
                <Code2 className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
                JSON (Code)
              </TabsTrigger>
              <TabsTrigger
                value="json-tree"
                className="text-xs px-3 h-6"
                data-testid="expanded-edit-json-view-tree"
                disabled={!parsedJsonIsContainer}
              >
                <ListTree className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
                JSON (Tree)
              </TabsTrigger>
              <TabsTrigger
                value="text"
                className="text-xs px-3 h-6"
                data-testid="expanded-edit-text-tab"
              >
                <FileText className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
                Text
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {/* Editor */}
        <div className="flex-1 min-h-0 border rounded-lg overflow-hidden">
          {mode === 'json' ? (
            jsonView === 'tree' && parsedJson.ok && parsedJsonIsContainer ? (
              <div
                className="h-[350px] w-full overflow-auto bg-background"
                data-testid="expanded-edit-json-tree"
              >
                <JsonTreeView
                  value={parsedJson.value}
                  // Explicitly expand only the root container; depth-1
                  // containers (e.g. `address`) stay collapsed until the
                  // user expands them. The component default is 2 — the
                  // dialog deliberately opts into the depth-1 contract.
                  defaultExpandDepth={1}
                  onChange={
                    isEditing
                      ? (next) => {
                          // Round-trip through the dialog's `value` string so
                          // the existing JSON.parse/stringify pipeline (Save
                          // button, validation effect) keeps working
                          // unchanged. Pretty-print to match what the Code
                          // view emits when entering JSON mode.
                          setValue(JSON.stringify(next, null, 2));
                        }
                      : undefined
                  }
                  onEditTelemetry={
                    isEditing
                      ? (kind) => {
                          track('feature:json_cell_edit_save', {
                            change_kind: kind,
                            ...(declaredJsonContext?.engine
                              ? { engine: declaredJsonContext.engine }
                              : {}),
                          });
                        }
                      : undefined
                  }
                />
              </div>
            ) : (
              <Editor
                height="350px"
                language="json"
                value={value}
                onChange={isEditing ? (newValue) => setValue(newValue ?? '') : undefined}
                options={{
                  readOnly: !isEditing,
                  minimap: { enabled: false },
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  wordWrap: 'on',
                  fontSize: 13,
                  automaticLayout: true,
                  tabSize: 2,
                  // I2 (fix round 1) — this mount is JSON, not SQL/PL-SQL
                  // (the sweep's named scope), but the same three guide
                  // values are equally useful for JSON's nested structure
                  // and there's no principled reason for this mount to
                  // differ from every other Monaco mount in the app.
                  guides: { indentation: false, highlightActiveIndentation: true, bracketPairs: true },
                }}
                theme={darkMode ? 'vs-dark' : 'vs'}
                beforeMount={ensureJamMonacoThemes}
              />
            )
          ) : (
            <Textarea
              ref={textareaRef}
              value={value}
              onChange={isEditing ? (e) => setValue(e.target.value) : undefined}
              readOnly={!isEditing}
              spellCheck={false}
              className="h-[350px] w-full resize-none font-mono text-sm border-0 rounded-none focus-visible:ring-0"
              placeholder="Enter text…"
              data-testid="expanded-edit-textarea"
            />
          )}
        </div>

        {/* JSON Validation Error */}
        {isEditing && mode === 'json' && jsonError && (
          <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 rounded-md p-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span className="truncate">{jsonError}</span>
          </div>
        )}

        {/* Stats */}
        <div className="text-xs text-muted-foreground tabular-nums">
          {stats.lines} {stats.lines === 1 ? 'line' : 'lines'} • {stats.chars} {stats.chars === 1 ? 'character' : 'characters'}
        </div>

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button variant="ghost" onClick={handleCancel}>
            <X className="h-4 w-4 mr-2" aria-hidden="true" />
            {isEditing ? 'Cancel' : 'Close'}
          </Button>
          <div className="flex items-center gap-3">
            {/* "Undeclare as JSON" — only when opened from a user-declared
                JSON column (resolved via MetaInfo lookup above). Native JSON
                columns won't match a JsonColumnDeclaration so the link
                stays hidden. */}
            {matchingJsonDeclaration && (
              <button
                type="button"
                className="text-xs text-muted-foreground underline-offset-2 hover:underline hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                onClick={() =>
                  openConfirmDialog({
                    title: 'Undeclare as JSON?',
                    description: declaredJsonContext
                      ? `Remove the JSON-column declaration for ${declaredJsonContext.schema}.${declaredJsonContext.table}.${declaredJsonContext.column}? Filters and the tree-view cell renderer will fall back to plain-text behavior.`
                      : undefined,
                    confirmLabel: 'Undeclare',
                    variant: 'destructive',
                    onConfirm: handleUndeclareJson,
                  })
                }
                data-testid="expanded-edit-undeclare-json"
              >
                Undeclare as JSON
              </button>
            )}
            {isEditing ? (
              <Button
                onClick={handleConfirm}
                disabled={mode === 'json' && !!jsonError}
                data-testid="expanded-edit-save"
              >
                <Check className="h-4 w-4 mr-2" aria-hidden="true" />
                Save
              </Button>
            ) : onRequestEdit ? (
              <Button
                onClick={() => {
                  setIsEditing(true);
                  onRequestEdit?.();
                }}
                data-testid="expanded-edit-start-edit"
              >
                <Pencil className="h-4 w-4 mr-2" aria-hidden="true" />
                Edit
              </Button>
            ) : null}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
